import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { getSDK } from '../services/sdk-instance';

// The callback is an API route, so it lives on the API origin — not the page's, which differs on two-host stacks (la ADR-8588f).
export function oauthCallbackUrl(): string {
  return `${getSDK().baseUrl}/v1/integrations/oauth/callback`;
}
import { holdsIdentityAtom } from '../stores/auth/atoms';
import { sdkConfiguredAtom } from '../stores/sdk/atoms';
import type { CreateConnectionParams, TestActionParams } from '../types/integrations-types';

export function useCatalog(params?: { q?: string; category?: string; scope?: string }) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  return useQuery({
    queryKey: ['integrations', 'catalog', params],
    queryFn: () => getSDK().integrations.searchCatalog(params),
    enabled: isAuthenticated && sdkConfigured,
  });
}

export function useCatalogEntry(slug: string | undefined) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  return useQuery({
    queryKey: ['integrations', 'catalog', slug],
    queryFn: () => getSDK().integrations.getCatalogEntry(slug!),
    enabled: Boolean(isAuthenticated && sdkConfigured && slug),
  });
}

export function useDefinition(definitionId: string | undefined) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  return useQuery({
    queryKey: ['integrations', 'definitions', definitionId],
    queryFn: () => getSDK().integrations.getDefinition(definitionId!),
    enabled: Boolean(isAuthenticated && sdkConfigured && definitionId),
  });
}

export function useDefinitionVersions(definitionId: string | undefined) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  return useQuery({
    queryKey: ['integrations', 'definitions', definitionId, 'versions'],
    queryFn: () => getSDK().integrations.listVersions(definitionId!),
    enabled: Boolean(isAuthenticated && sdkConfigured && definitionId),
  });
}

export function useDefinitionVersion(definitionId: string | undefined, versionId: string | undefined) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  return useQuery({
    queryKey: ['integrations', 'definitions', definitionId, 'versions', versionId],
    queryFn: () => getSDK().integrations.getVersion(definitionId!, versionId!),
    enabled: Boolean(isAuthenticated && sdkConfigured && definitionId && versionId),
  });
}

export function useConnections(params?: { status?: string; workspace_id?: string }) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  return useQuery({
    queryKey: ['integrations', 'connections', params],
    queryFn: () => getSDK().integrations.listConnections(params),
    enabled: isAuthenticated && sdkConfigured,
  });
}

export function useConnection(connectionId: string | undefined) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  return useQuery({
    queryKey: ['integrations', 'connections', connectionId],
    queryFn: () => getSDK().integrations.getConnection(connectionId!),
    enabled: Boolean(isAuthenticated && sdkConfigured && connectionId),
  });
}

export function useConnectionLogs(connectionId: string | undefined, params?: { limit?: number; offset?: number; status?: string }) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  return useQuery({
    queryKey: ['integrations', 'connections', connectionId, 'logs', params],
    queryFn: () => getSDK().integrations.getConnectionLogs(connectionId!, params),
    enabled: Boolean(isAuthenticated && sdkConfigured && connectionId),
  });
}

export function useConnectionHealth(connectionId: string | undefined) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  return useQuery({
    queryKey: ['integrations', 'connections', connectionId, 'health'],
    queryFn: () => getSDK().integrations.getConnectionHealth(connectionId!),
    enabled: Boolean(isAuthenticated && sdkConfigured && connectionId),
    refetchInterval: 60000,
  });
}

export function useCreateConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: CreateConnectionParams) => getSDK().integrations.createConnection(params),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['integrations', 'connections'] });
      void queryClient.invalidateQueries({ queryKey: ['integrations', 'catalog'] });
    },
  });
}

export function useDeleteConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (connectionId: string) => getSDK().integrations.deleteConnection(connectionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['integrations', 'connections'] });
      void queryClient.invalidateQueries({ queryKey: ['integrations', 'catalog'] });
    },
  });
}

export function useDisableConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (connectionId: string) => getSDK().integrations.disableConnection(connectionId),
    onSuccess: (_data, connectionId) => {
      void queryClient.invalidateQueries({ queryKey: ['integrations', 'connections', connectionId] });
      void queryClient.invalidateQueries({ queryKey: ['integrations', 'connections'] });
    },
  });
}

export function useEnableConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (connectionId: string) => getSDK().integrations.enableConnection(connectionId),
    onSuccess: (_data, connectionId) => {
      void queryClient.invalidateQueries({ queryKey: ['integrations', 'connections', connectionId] });
      void queryClient.invalidateQueries({ queryKey: ['integrations', 'connections'] });
    },
  });
}

export function useUpgradeConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ connectionId, targetVersionId }: { connectionId: string; targetVersionId: string }) =>
      getSDK().integrations.upgradeConnection(connectionId, { target_version_id: targetVersionId }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['integrations', 'connections', variables.connectionId] });
      void queryClient.invalidateQueries({ queryKey: ['integrations', 'connections'] });
    },
  });
}

export function useAuthenticateApiKey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ connectionId, apiKey }: { connectionId: string; apiKey: string }) =>
      getSDK().integrations.authenticateAPIKey(connectionId, { api_key: apiKey }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['integrations', 'connections', variables.connectionId] });
      void queryClient.invalidateQueries({ queryKey: ['integrations', 'connections'] });
    },
  });
}

export function useAuthenticateClientCredentials() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ connectionId, clientId, clientSecret }: { connectionId: string; clientId: string; clientSecret: string }) =>
      getSDK().integrations.authenticateClientCredentials(connectionId, { client_id: clientId, client_secret: clientSecret }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['integrations', 'connections', variables.connectionId] });
      void queryClient.invalidateQueries({ queryKey: ['integrations', 'connections'] });
    },
  });
}

export function useInitiateOAuth2() {
  return useMutation({
    mutationFn: ({ connectionId, redirectUri }: { connectionId: string; redirectUri: string }) =>
      getSDK().integrations.initiateOAuth2(connectionId, { redirect_uri: redirectUri }),
  });
}

export function useTestAction() {
  return useMutation({
    mutationFn: ({ connectionId, params }: { connectionId: string; params: TestActionParams }) =>
      getSDK().integrations.testAction(connectionId, params),
  });
}
